import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, BriefcaseBusiness, Building2, HeartHandshake, MapPin, ShieldCheck, Stethoscope } from "lucide-react";
import { parseDelimitedItems, parseKeyValueMap, parseSimpleList } from "@/lib/pageContent";
import { usePublicSettings } from "@/hooks/usePublicSiteData";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { getPublicUiCopy } from "@/lib/publicPageLocalization";

export default function FounderProfile() {
  const { language } = useSiteLanguage();
  const { data: settings } = usePublicSettings();
  const ui = getPublicUiCopy(language, "founderProfile");

  const founderName = settings?.founder_name || "Shankar Kumar Howlader";
  const highlights = parseSimpleList(
    settings?.founder_profile_highlights ||
      "Founder of R.S. International\nEstablished the company in 1985\nPioneer in chemical and medical imports\nRadiographer and public servant"
  );
  const storyParagraphs = parseSimpleList(settings?.founder_profile_story_paragraphs || "");
  const legacyParagraphs = parseSimpleList(settings?.founder_profile_legacy_paragraphs || "");
  const timeline = parseDelimitedItems(settings?.founder_profile_timeline_items || "");
  const focusItems = parseSimpleList(settings?.founder_profile_focus_items || "");
  const commonLabels = parseKeyValueMap(settings?.profile_common_labels || "");
  const sectionLabels = parseKeyValueMap(settings?.founder_profile_section_labels || "");

  return (
    <>
      <section className="bg-[var(--rsi-bg-subtle)] py-20 lg:py-24" data-testid="founder-profile-hero">
        <div className="container-rsi grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <Link to="/about" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--rsi-purple)]">
              <ArrowLeft className="h-4 w-4" /> {commonLabels.back || "Back to About"}
            </Link>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--rsi-purple-tint)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--rsi-purple)]">
              <BriefcaseBusiness className="h-3.5 w-3.5" /> {settings?.founder_profile_badge || "Founder Profile"}
            </div>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl font-black tracking-tighter text-[var(--rsi-navy)]">
              {founderName}
            </h1>
            <p className="mt-3 text-lg font-medium text-[var(--rsi-purple)]">
              {settings?.founder_profile_title || "Founder of R.S. International"}
            </p>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-700">
              {settings?.founder_profile_intro ||
                "Shankar Kumar Howlader founded R.S. International in 1985 and gave the organization its first commercial identity through honesty, practical medical-sector knowledge, and disciplined business relationships. His life combined entrepreneurship, professional healthcare service, and public duty."}
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {highlights.map((item) => (
                <div key={item} className="rounded-2xl border border-white bg-white px-4 py-4 shadow-sm text-sm font-semibold text-[var(--rsi-navy)]">
                  {item}
                </div>
              ))}
            </div>
          </motion.div>

          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-[28px] border border-[var(--rsi-purple)]/10 bg-white shadow-2xl shadow-[var(--rsi-purple)]/10">
              <div className="h-24 bg-gradient-to-r from-[var(--rsi-purple)] to-[var(--rsi-navy)]" />
              <div className="px-6 pb-6 -mt-12">
                <div className="overflow-hidden rounded-3xl border-4 border-white shadow-xl">
                  <img
                    src={settings?.founder_photo_url || "/founder-shankar-howlader.jpg"}
                    alt={founderName}
                    className="h-[460px] w-full object-cover object-top"
                  />
                </div>
                <div className="mt-5 grid gap-3 text-sm text-gray-600">
                  <div className="inline-flex items-center gap-2 rounded-full bg-[var(--rsi-bg-subtle)] px-3 py-2 w-fit">
                    <Building2 className="h-4 w-4 text-[var(--rsi-purple)]" /> {ui.foundedIn} {settings?.company_since || "1985"}
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-[var(--rsi-bg-subtle)] px-3 py-2 w-fit">
                    <MapPin className="h-4 w-4 text-[var(--rsi-purple)]" /> {settings?.founder_profile_office_address || "215/3 Lalbag Road, Dhaka - 1211"}
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-[var(--rsi-bg-subtle)] px-3 py-2 w-fit">
                    <Stethoscope className="h-4 w-4 text-[var(--rsi-purple)]" /> {settings?.founder_profile_profession_label || "Radiographer"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="founder-profile-content">
        <div className="container-rsi grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-8">
            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <ShieldCheck className="h-4 w-4" /> {sectionLabels.story || "Life Story"}
              </div>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
                {settings?.founder_profile_story_title || "A founder whose work shaped the earliest identity of the company"}
              </h2>
              <div className="mt-5 space-y-4 text-gray-700 leading-relaxed">
                {storyParagraphs.map((item) => <p key={item}>{item}</p>)}
              </div>
            </div>

            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <HeartHandshake className="h-4 w-4" /> {sectionLabels.legacy || "Legacy"}
              </div>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
                {settings?.founder_profile_legacy_title || "Entrepreneurship, service, and professional duty"}
              </h2>
              <div className="mt-5 space-y-4 text-gray-700 leading-relaxed">
                {legacyParagraphs.map((item) => <p key={item}>{item}</p>)}
              </div>
            </div>

            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <Building2 className="h-4 w-4" /> {sectionLabels.timeline || "Timeline"}
              </div>
              <div className="mt-6 space-y-5">
                {timeline.map((item) => (
                  <div key={item.title} className="rounded-3xl border border-gray-100 p-6">
                    <div className="font-display text-xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
                      {item.title}
                    </div>
                    <div className="mt-3 text-sm leading-relaxed text-gray-700">{item.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-8">
            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <MapPin className="h-4 w-4" /> {sectionLabels.facts || "Founder Facts"}
              </div>
              <div className="mt-5 space-y-4">
                <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] px-5 py-4">
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-400">{ui.birth}</div>
                  <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                    {settings?.founder_profile_fact_birth || "01 March 1965"}
                  </div>
                </div>
                <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] px-5 py-4">
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-400">{ui.passing}</div>
                  <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                    {settings?.founder_profile_fact_passing || "14 Dec 2018, 3:29 AM"}
                  </div>
                </div>
                <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] px-5 py-4">
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-400">{ui.officePhone}</div>
                  <div className="mt-2 font-mono-plex text-xl font-bold tracking-[0.08em] text-[var(--rsi-navy)]">
                    {settings?.founder_profile_fact_office_phone || "500999 & 323195"}
                  </div>
                </div>
              </div>
            </div>

            <div className="rsi-card p-8">
              <div className="inline-flex items-center gap-2 text-[var(--rsi-purple)] font-semibold">
                <Stethoscope className="h-4 w-4" /> {sectionLabels.focus || "Early Business Focus"}
              </div>
              <div className="mt-5 space-y-3">
                {focusItems.map((item) => (
                  <div key={item} className="rounded-2xl border border-gray-100 px-5 py-4 text-sm text-gray-700">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
